import axios from "../../api/axiosInstance";
import { STATUS, extractErrorInfo } from "./sharedfunction";

/**
 * List API tokens for the current user (metadata only)
 * @returns {Promise<Object>} Response object with status, data (array of tokens), and message
 */
export async function listApiTokens() {
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/user/api-tokens`,
      { withCredentials: true }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || [],
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
    };
  } catch (error) {
    console.error("Error listing API tokens:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Create a new API token
 * @param {Object} params
 * @param {string} params.name - Token label (e.g. "Cursor MCP")
 * @param {number} [params.expiresInDays] - Days until expiration (default: 90)
 * @param {string} [params.slackLinkToken] - present only when this token is being created
 *   to complete a pending Slack account-link (see the `slackLinkToken` URL param on this page)
 * @returns {Promise<Object>} Response object with status, data (includes token shown once), and message
 */
export async function createApiToken({ name, expiresInDays = 90, slackLinkToken } = {}) {
  try {
    if (!name || name.trim().length === 0) {
      return {
        status: STATUS.ERROR,
        error: "Token name is required",
      };
    }

    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/user/api-tokens`,
      { name: name.trim(), expiresInDays, ...(slackLinkToken ? { slackLinkToken } : {}) },
      { withCredentials: true }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
    };
  } catch (error) {
    console.error("Error creating API token:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Revoke an API token by id (session required; CSRF sent via axios interceptor).
 * @param {string} tokenId - Token UUID
 * @returns {Promise<Object>} Response object with status, data (e.g. { revoked }), and message
 */
export async function revokeApiToken(tokenId) {
  try {
    const response = await axios.delete(
      `${process.env.REACT_APP_API_URL}/user/api-tokens/${tokenId}`,
      { withCredentials: true }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
    };
  } catch (error) {
    console.error("Error revoking API token:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Permanently delete an API token by id. Server enforces that the token is REVOKED or EXPIRED.
 * @param {string} tokenId - Token UUID
 * @returns {Promise<Object>} Response object with status, data (e.g. { deleted }), and message
 */
export async function deleteApiToken(tokenId) {
  try {
    const response = await axios.delete(
      `${process.env.REACT_APP_API_URL}/user/api-tokens/${tokenId}/purge`,
      { withCredentials: true }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
    };
  } catch (error) {
    console.error("Error deleting API token:", error);
    return extractErrorInfo(error);
  }
}
